import { forwardRef } from 'react'
import {
  Radio as BaseRadio,
  Tab as BaseTab,
  TableCell as BaseTableCell,
  Tabs as BaseTabs,
  Tooltip as BaseTooltip
} from '@chilecompra/react-kit/components'
import {
  Close as BaseCloseIcon,
  FileDownload as BaseFileDownloadIcon,
  FileUpload as BaseFileUploadIcon,
  Help as BaseHelpIcon,
  InsertDriveFileOutlined as BaseFileIcon
} from '@mui/icons-material'
import { Divider as BaseDivider, styled, TableContainer as BaseTableContainer } from '@mui/material'

import { BaseContainer, BaseItem } from '../../config/styles/BaseGrid'

export const AttachedFiles = styled(BaseContainer)``

export const Container = styled(BaseContainer)``

export const DialogButtons = styled(BaseContainer)``

export const DialogContent = styled(BaseContainer)`
  &.MuiGrid-container {
    ${props => props.theme.breakpoints.only('sm')} {
      padding: 32px 16px;
    }

    ${props => props.theme.breakpoints.only('xs')} {
      padding: 32px 0;
    }
  }
`

export const DialogMessage = styled(BaseContainer)`
  &.MuiGrid-container {
    max-height: 575px;
    overflow: auto;
    padding-inline-end: 8px;
  }
`

export const Divider = styled(({ margin, ...props }) => <BaseDivider {...props} />)`
  &.MuiDivider-root {
    margin: ${props => props.margin};
  }
`

export const DownloadIcon = styled(BaseFileDownloadIcon)`
  &.MuiSvgIcon-root {
    color: ${props => props.theme.palette.blue[50]};
    height: 16px;
    width: 16px;
  }
`

export const DownloadLink = styled('a')`
  align-items: center;
  color: ${props => props.theme.palette.brand.primary};
  cursor: pointer;
  display: inline-flex;
  font-family: ${props => props.theme.typography.fontFamily};
  font-size: 14px;
  font-weight: 500;
  gap: 8px;
  justify-content: center;
  text-decoration: underline;

  &:hover {
    color: ${props => props.theme.palette.blue[30]};
  }
`

export const FileCard = styled('div')`
  align-items: center;
  background-color: ${props => props.theme.palette.brand.white};
  border: 1px solid ${props => props.theme.palette.brand.gray2};
  border-radius: 8px;
  display: flex;
  gap: 12px;
  padding: 16px;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;
  width: 100%;

  &:hover {
    border-color: ${props => props.theme.palette.brand.primary};
    box-shadow: 0 2px 8px rgba(0, 100, 255, 0.08);
  }
`

export const FileIcon = styled(BaseFileIcon)`
  &.MuiSvgIcon-root {
    color: ${props => props.theme.palette.brand.primary};
    height: 24px;
    width: 24px;
  }
`

export const FileInfo = styled(BaseContainer)`
  flex-direction: column;
  flex: 1;

  .file__name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`

export const Files = styled(BaseItem)``

export const FileUploadIcon = styled(BaseFileUploadIcon)`
  &.MuiSvgIcon-root {
    margin-left: 4px;
    height: 18px;
    width: 18px;
  }
`

export const FileUploadInfo = styled(BaseContainer)``

export const FileUploadWrapper = styled(BaseContainer)``

export const HiddenInput = styled('input')`
  display: none;
`

export const Item = styled(BaseItem)`
  ${props => props.theme.breakpoints.down('lg')} {
    &.form {
      order: 2;
    }

    &.info-panel {
      order: 1;
    }
  }
`

export const MoreInformationLink = styled('div')`
  color: ${props => props.theme.palette.brand.primary};
  cursor: pointer;
  font-family: ${props => props.theme.typography.fontFamily};
  font-size: 14px;
  font-weight: 500;
  margin-top: 6px;
  text-decoration: underline;

  &:hover {
    color: ${props => props.theme.palette.blue[30]};
  }
`

export const Radio = styled(BaseRadio)`
  &.MuiFormGroup-root {
    &[direction='horizontal'] {
      align-items: center;
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      gap: 32px;
      margin: 0;

      .MuiFormControlLabel-root {
        gap: 16px;

        .MuiFormControlLabel-label {
          margin: 0;
        }
      }
    }
  }
`

export const RemoveIcon = styled(({ disabled, ...props }) => <BaseCloseIcon {...props} />)`
  &.MuiSvgIcon-root {
    background-color: transparent;
    border-radius: 50%;
    color: ${props =>
      props.disabled ? props.theme.palette.brand.disableFont : props.theme.palette.brand.black3};
    cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
    flex-shrink: 0;
    height: 20px;
    padding: 4px;
    pointer-events: ${props => (props.disabled ? 'none' : 'visible')};
    transition:
      background-color 0.2s ease,
      color 0.2s ease;
    width: 20px;

    &:hover {
      background-color: ${props => !props.disabled && 'rgba(239, 68, 68, 0.1)'};
      color: ${props => !props.disabled && props.theme.palette.brand.error};
    }
  }
`

export const Root = styled(BaseContainer)``

export const SuccessIcon = styled('img')`
  height: 64px;
  pointer-events: none;
  user-select: none;
  width: 64px;
`

export const Tab = styled(BaseTab)`
  height: 220px;
  min-height: 220px;
  overflow: auto;
`

export const TableCell = styled(({ isAdded, padding, ...props }) => <BaseTableCell {...props} />)`
  &.MuiTableCell-root {
    padding: ${props => props.padding && `${props.padding} !important`};
    vertical-align: middle;
  }

  &.cellHeader {
    background-color: ${props => props.theme.palette.brand.gray3};
  }
`

export const TableContainer = styled(BaseTableContainer)`
  max-width: 100%;

  ${props => props.theme.breakpoints.up('lg')} {
    max-width: 75%;
  }

  .MuiTable-root {
    border: 1px solid ${props => props.theme.palette.brand.gray2};

    &:focus,
    &:focus-visible {
      border: 1px solid ${props => props.theme.palette.brand.gray2};
    }
  }
`

export const Tabs = styled(BaseTabs)`
  &.MuiTabs-root .MuiTab-root {
    padding: 0;
  }
`

export const Tooltip = styled(BaseTooltip)`
  &.MuiPopper-root .MuiTooltip-tooltip {
    max-height: 100%;

    > div {
      max-height: 100%;
      word-break: auto-phrase;
    }
  }
`

export const TooltipIcon = styled(
  forwardRef(({ size, ...props }, ref) => <BaseHelpIcon {...props} ref={ref} />)
)`
  &.MuiSvgIcon-root {
    fill: ${props => props.theme.palette.gray[50]};
    font-size: ${props => {
      if (props.size === 'large') {
        return '1.2rem'
      }

      return '0.8rem'
    }};
    margin: 0 0 0 16px;
  }
`

export const TooltipText = styled('span')`
  margin: 0;
`
